import PerlinNoise3D from "./noise";
import Mat4 from "./mat4";
import { SceneParameters, SceneParameter } from "./scenes";
import { Shape } from "./geometryShapes";
import { ShapeDisplayMode, ShapeInfo } from "./geometryScenes";

import * as bodySegmentation from '@tensorflow-models/body-segmentation';
import '@tensorflow/tfjs-core';
import '@tensorflow/tfjs-converter';
import '@tensorflow/tfjs-backend-webgl';

const basicVs = `
attribute vec4 a_vertexPosition;
varying vec2 v_position;

void main() {
    gl_Position = a_vertexPosition;
    v_position = vec2(gl_Position.x * 0.5 + 0.5, - gl_Position.y * 0.5 + 0.5);
}
`;

const textureVs = `
attribute vec4 a_vertexPosition;
varying vec2 v_position;

void main() {
    gl_Position = a_vertexPosition;
    v_position = vec2(gl_Position.x * 0.5 + 0.5, gl_Position.y * 0.5 + 0.5);
}
`;

const basicShapeVs = `
    attribute vec4 a_vertexPosition;
    attribute vec3 a_vertexNormal;
    attribute vec3 a_texCoords;
    uniform mat4 u_modelViewMatrix;
    uniform mat4 u_projectionMatrix;
    uniform mat4 u_normalMatrix;
    varying highp vec4 v_localPosition;
    varying highp vec4 v_globalPosition;
    varying highp vec3 v_texCoord;
    varying highp float directionaLightA;
    varying highp float directionaLightB;
    void main() {
        gl_Position = u_projectionMatrix * u_modelViewMatrix * a_vertexPosition;
        v_localPosition = a_vertexPosition;
        v_globalPosition = gl_Position;
        highp vec4 transformedNormal = u_normalMatrix * vec4(a_vertexNormal, 1.0);
        v_texCoord = a_texCoords;
        highp vec3 directionalLightAVector = normalize(vec3(0.85, -0.8, 0.15));
        highp vec3 directionalLightBVector = normalize(vec3(0.85, 0.8, -0.75));
        directionaLightA = max(dot(transformedNormal.xyz, directionalLightAVector), 0.0);
        directionaLightB = max(dot(transformedNormal.xyz, directionalLightBVector), 0.0);
    }
`;

const basicFs = `
precision mediump float;
varying vec2 v_position;

uniform int u_time;
uniform vec2 u_canvasSize;
uniform vec2 u_cursorPosition;

void main() {
    float distToCursor = distance(v_position.xy * u_canvasSize, u_cursorPosition) * 2. / min(u_canvasSize.x, u_canvasSize.y);
    gl_FragColor = vec4(v_position.x - distToCursor, v_position.y - distToCursor, 1.0 - distToCursor, 1.0);
}
`

export enum StateType {
    "FadeIn" = 0,
    'Normal',
    "FadeOut",
}

export type AudioReactiveParameters = {
    beatDetected: boolean,
    beatCount: number,
    beatFade: number,
    energy: number,
    levels: Uint8Array | Float32Array,
}

export type AnimationParameters = {
    time: number,
    canvasWidth: number, 
    canvasHeight: number,
    cursorX: number,
    cursorY: number,
    state: number,
    anchorTime: number,
    pressure: number,
    audioReactiveParameters?: AudioReactiveParameters,
    parameters?: SceneParameter[],
    particleX?: number,
    particleY?: number,
}

export type SamplerFunctionParameters = AnimationParameters & {
    context: CanvasRenderingContext2D,
    storage: Record<string, any>,
};

export type SamplerFunction = (samplerFunctionParameters: SamplerFunctionParameters) => void 

export type Sampler = {
    states: Array<StateType>,
    sampleType: 'Self' | 'Canvas' | 'Camera' | 'Function' | 'BodySegmentation',
    shapeSampler: boolean,
    shapeGroup?: number,
    sampleSource?: HTMLCanvasElement | HTMLVideoElement,
    samplerCanvas?: HTMLCanvasElement,
    samplerCtx?: CanvasRenderingContext2D,
    samplerFunction?: SamplerFunction,
    samplerStorage?: Record<string, any>,
}

class ArtistWebgl {
    canvas: HTMLCanvasElement;
    context: WebGLRenderingContext;
    canvasWidth: number;
    canvasHeight: number;

    vsSource: string;
    fsSource: string;

    parameters?: Array<SceneParameter>;

    programInfo: {
        program: WebGLProgram | null,
        attribLocations: any,
        uniformLocations: any,
    } | null;
    buffers: any;

    cursorX: number;
    cursorY: number;
    pointerPressure: number;

    state: StateType;
    anchorTime: number;

    layoutSampler?: HTMLCanvasElement;
    samplers: Array<Sampler>;
    artistSamplers: Array<Sampler>;
    shapeSamplers: Array<Sampler>;

    framebuffer?: WebGLFramebuffer | null;
    depthBuffer?: WebGLRenderbuffer | null;
    postProcessingProgramInfo?: {
        program: WebGLProgram | null,
        attribLocations: any,
        uniformLocations: any
    } | null;
    postProcessing?: string;

    shapes?: Array<ShapeInfo>;
    shapeShaders?: Array<ShapeShader>;

    constructor(canvas: HTMLCanvasElement, vertexShaderSource = basicVs, fragmentShaderSource = basicFs, sceneParameters?: SceneParameters, layoutSampler?: HTMLCanvasElement, samplers: Array<Sampler> = [], postProcessing?: string, shapes?: Array<ShapeInfo>) {
        this.canvas = canvas
        this.context = canvas.getContext('webgl', { antialias: true })! as WebGLRenderingContext;
        this.canvas.style.cursor = "none";
        this.canvasWidth = this.context.canvas.width;
        this.canvasHeight = this.context.canvas.height;
        this.cursorX = 0;
        this.cursorY = 0;
        this.pointerPressure = 0;
        this.anchorTime = 0;
        this.state = StateType.FadeIn;

        this.vsSource = vertexShaderSource;
        this.fsSource = fragmentShaderSource;

        this.parameters = sceneParameters? this.convertParameters(sceneParameters): undefined;

        this.layoutSampler = layoutSampler;

        this.samplers = samplers;
        this.artistSamplers = samplers.filter(sampler => !sampler.shapeSampler);
        this.shapeSamplers = samplers.filter(sampler => sampler.shapeSampler);
        this.samplers.forEach(async sampler => {
            sampler.samplerCanvas = document.createElement("canvas");
            sampler.samplerCanvas.width = this.canvasWidth;
            sampler.samplerCanvas.height = this.canvasHeight;
            sampler.samplerCtx = sampler.samplerCanvas.getContext('2d')!;
            switch(sampler.sampleType) {
                case "Self":
                    sampler.sampleSource = this.canvas;
                    break;
                case "Canvas":
                    break;
                case "Camera":
                    break;
                case "Function":
                    sampler.samplerStorage = {};
                    break;
                case "BodySegmentation":
                    sampler.samplerStorage = {};
                    break;
            }
        })

        this.programInfo = this.initShaderProgram(this.vsSource, this.fsSource);
        
        this.postProcessing = postProcessing;
        if (this.postProcessing) {
            this.postProcessingProgramInfo = this.initShaderProgram(textureVs, this.postProcessing);
        }
        
        this.shapes = shapes;
        if (shapes) {
            this.shapeShaders = shapes.map((shapeGroup, index) => 
                new ShapeShader(
                    //this.canvas, 
                    this.context, 
                    shapeGroup.shapes, 
                    shapeGroup.vsSource, 
                    shapeGroup.fsSource, 
                    shapeGroup.displayMode, 
                    shapeGroup.bgShader,
                    shapeGroup.overlayShader,
                    this.shapeSamplers.filter(sampler => sampler.shapeGroup === undefined || sampler.shapeGroup === index),
                    this.parameters,
                    shapeGroup.shapeAnimation
                )
            )
        }
        
        this.buffers = this.initBuffers();
    }

    convertParameters(sceneParameters: SceneParameters) {
        const parameters: Array<SceneParameter> = []
        sceneParameters.forEach(box => {
            box.parameters.forEach(parameter => {
                parameters.push(parameter);
            });
        });
        return parameters;
    }

    reloadShader(vertexShaderSource = basicVs, fragmentShaderSource = basicFs, sceneParameters?: SceneParameters, samplers: Array<Sampler> = [], postProcessing?: string, shapes?: Array<ShapeInfo>) {
        this.vsSource = vertexShaderSource;
        this.fsSource = fragmentShaderSource;
        this.parameters = sceneParameters? this.convertParameters(sceneParameters): undefined;

        this.samplers = samplers;
        this.artistSamplers = samplers.filter(sampler => !sampler.shapeSampler);
        this.shapeSamplers = samplers.filter(sampler => sampler.shapeSampler);
        this.samplers.forEach(async sampler => {
            sampler.samplerCanvas = document.createElement("canvas");
            sampler.samplerCanvas.width = this.canvasWidth;
            sampler.samplerCanvas.height = this.canvasHeight;
            sampler.samplerCtx = sampler.samplerCanvas.getContext('2d')!;
            switch(sampler.sampleType) {
                case "Self":
                    sampler.sampleSource = this.canvas;
                    break;
                case "Canvas":
                    break;
                case "Camera":
                    break;
                case "Function":
                    sampler.samplerStorage = {};
                    break;
                case "BodySegmentation":
                    sampler.samplerStorage = {};
                    break;
            }
        })

        this.programInfo = this.initShaderProgram(this.vsSource, this.fsSource);
        this.postProcessing = postProcessing;
        if (this.postProcessing) {
            this.postProcessingProgramInfo = this.initShaderProgram(textureVs, this.postProcessing);
        } else {
            this.postProcessingProgramInfo = undefined;
        }
        this.shapes = shapes;
        if (shapes) {
            this.shapeShaders = shapes.map((shapeGroup, index) => 
                new ShapeShader(
                    //this.canvas, 
                    this.context, 
                    shapeGroup.shapes, 
                    shapeGroup.vsSource, 
                    shapeGroup.fsSource, 
                    shapeGroup.displayMode, 
                    shapeGroup.bgShader,
                    shapeGroup.overlayShader,
                    this.shapeSamplers.filter(sampler => sampler.shapeGroup === undefined || sampler.shapeGroup === index),
                    this.parameters,
                    shapeGroup.shapeAnimation
                )
            )
        } else {
            this.shapeShaders = undefined;
        }
        this.buffers = this.initBuffers();
    }

    updateCanvasSize(width: number, height: number) {
        this.canvasWidth = width;
        this.canvasHeight = height;
        this.context.canvas.width  = width;
        this.context.canvas.height = height;

        this.samplers.forEach(sampler => {
            sampler.samplerCanvas!.width = width;
            sampler.samplerCanvas!.height = height;
        })

        this.context.viewport(0, 0, width, height);
        if (this.framebuffer && this.buffers.frameBufferTexture) {
            this.context.bindTexture(this.context.TEXTURE_2D, this.buffers.frameBufferTexture);
            this.context.texImage2D(this.context.TEXTURE_2D, 0, this.context.RGBA, 
                                    this.canvasWidth, this.canvasHeight, 0, 
                                    this.context.RGBA, this.context.UNSIGNED_BYTE, null);
            this.context.bindFramebuffer(this.context.FRAMEBUFFER, this.framebuffer);
            this.context.framebufferTexture2D(this.context.FRAMEBUFFER, this.context.COLOR_ATTACHMENT0, this.context.TEXTURE_2D, this.buffers.frameBufferTexture, 0);
            
            if (this.shapes) {
                this.depthBuffer = this.context.createRenderbuffer();
                this.context.bindRenderbuffer(this.context.RENDERBUFFER, this.depthBuffer);
                this.context.renderbufferStorage(this.context.RENDERBUFFER, this.context.DEPTH_COMPONENT16, width, height);
                this.context.framebufferRenderbuffer(this.context.FRAMEBUFFER, this.context.DEPTH_ATTACHMENT, this.context.RENDERBUFFER, this.depthBuffer);
            }

            this.context.bindFramebuffer(this.context.FRAMEBUFFER, null);
            this.context.bindTexture(this.context.TEXTURE_2D, null);
        }
    }

    updateCursorPosition(cursorX: number, cursorY: number) {
        this.cursorX = cursorX;
        this.cursorY = cursorY;
    }

    updatePointerPressure(pointerDown: boolean, fps: number) {
        if (pointerDown) {
            this.pointerPressure += 1 / (fps * 0.3);
            this.pointerPressure = this.pointerPressure > 1 ? 1 : this.pointerPressure;
        } else {
            this.pointerPressure -= 1 / (fps * 0.3);
            this.pointerPressure = this.pointerPressure < 0 ? 0 : this.pointerPressure;
        }
    }

    changeState(state: StateType, anchorTime: number) {
        this.state = state;
        this.anchorTime = anchorTime;
    }

    loadProgram(vertexShaderSource: string, fragmentShaderSource: string) {
        const loadShader = (type: number, source: string) => {
            const shader = this.context.createShader(type);
            this.context.shaderSource(shader!, source);
            this.context.compileShader(shader!);
            if (!this.context.getShaderParameter(shader!, this.context.COMPILE_STATUS)) {
                alert(
                    `An error occurred compiling the shaders: ${this.context.getShaderInfoLog(shader!)}`,
                );
                this.context.deleteShader(shader);
                return null;
            }
            return shader;
        }
        const vertexShader = loadShader(this.context.VERTEX_SHADER, vertexShaderSource);
        const fragmentShader = loadShader(this.context.FRAGMENT_SHADER, fragmentShaderSource);
        const shaderProgram = this.context.createProgram();
        this.context.attachShader(shaderProgram!, vertexShader!);
        this.context.attachShader(shaderProgram!, fragmentShader!);
        this.context.linkProgram(shaderProgram!);
        if (!this.context.getProgramParameter(shaderProgram!, this.context.LINK_STATUS)) {
            alert(
                `Unable to initialize the shader program: ${this.context.getProgramInfoLog(
                shaderProgram!,
                )}`,
            );
            return null;
        }
        return shaderProgram;
    }
    
    initShaderProgram(vertexShaderSource: string, fragmentShaderSource: string) {
        const shaderProgram = this.loadProgram(vertexShaderSource, fragmentShaderSource);
        if (!shaderProgram) return null;
        const numParameters = this.parameters? this.parameters.length : 0;
        const numSamplers = this.artistSamplers.length;
        return {
            program: shaderProgram,
            attribLocations: {
                vertexPosition: this.context.getAttribLocation(shaderProgram, "a_vertexPosition"),
            },
            uniformLocations: ArtistWebgl.getCommonUniformLocations(this.context, shaderProgram, numParameters, numSamplers),
        };
    }

    static getCommonUniformLocations(context: WebGLRenderingContext, shaderProgram: WebGLProgram, numParameters: number, numSamplers: number) {
        const uniformLocations: any = {
            canvasSize: context.getUniformLocation(shaderProgram, "u_canvasSize"),
            cursorPosition: context.getUniformLocation(shaderProgram, "u_cursorPosition"),
            pointerPressure: context.getUniformLocation(shaderProgram, "u_pointerPressure"),
            state: context.getUniformLocation(shaderProgram, "u_state"),
            time: context.getUniformLocation(shaderProgram, "u_time"),
            anchorTime: context.getUniformLocation(shaderProgram, "u_anchorTime"),
            particlePosition: context.getUniformLocation(shaderProgram, "u_particlePosition"),
            layoutSampler: context.getUniformLocation(shaderProgram, "u_layoutSampler"),
            frameBufferSampler: context.getUniformLocation(shaderProgram, "u_frameBufferSampler"),
            audioReactiveOn: context.getUniformLocation(shaderProgram, "u_audioReactiveOn"),
            beatDetected: context.getUniformLocation(shaderProgram, "u_beatDetected"),
            beatCount: context.getUniformLocation(shaderProgram, "u_beatCount"),
            beatFade: context.getUniformLocation(shaderProgram, "u_beatFade"),
            energy: context.getUniformLocation(shaderProgram, "u_energy"),
        };
        for (let i = 0; i < numParameters; i++) {
            uniformLocations[`param${i}`] = context.getUniformLocation(shaderProgram, `u_param${i}`);
        }
        for (let i = 0; i < numSamplers; i++) {
            uniformLocations[`sampler${i}`] = context.getUniformLocation(shaderProgram, `u_sampler${i}`);
        }
        return uniformLocations
    }

    initBuffers() {
        const positionBuffer = this.context.createBuffer();
        this.context.bindBuffer(this.context.ARRAY_BUFFER, positionBuffer);
        const positions = [-1.0, 1.0, 1.0, 1.0, 1.0, 1.0, -1.0, -1.0, 1.0, 1.0, -1.0, 1.0];
        this.context.bufferData(this.context.ARRAY_BUFFER, new Float32Array(positions), this.context.STATIC_DRAW);
        const buffers: any = {};
        buffers.position = positionBuffer;

        if (this.layoutSampler) {
            const layoutSamplerTexture = this.context.createTexture();
            this.context.bindTexture(this.context.TEXTURE_2D, layoutSamplerTexture);
            this.context.texParameteri(this.context.TEXTURE_2D, this.context.TEXTURE_MIN_FILTER, this.context.LINEAR);
            this.context.texParameteri(this.context.TEXTURE_2D, this.context.TEXTURE_MAG_FILTER, this.context.LINEAR);
            this.context.texParameteri(this.context.TEXTURE_2D, this.context.TEXTURE_WRAP_S, this.context.CLAMP_TO_EDGE);
            this.context.texParameteri(this.context.TEXTURE_2D, this.context.TEXTURE_WRAP_T, this.context.CLAMP_TO_EDGE);
            this.context.texImage2D(this.context.TEXTURE_2D, 0, this.context.RGBA, this.context.RGBA, this.context.UNSIGNED_BYTE, this.layoutSampler);
            buffers.layoutSamplerTexture = layoutSamplerTexture;
        }

        if(this.artistSamplers.length) {
            buffers.samplerTextures = [];
            for (let i = 0; i < this.artistSamplers.length; i++) {
                const texture = this.context.createTexture();
                this.context.bindTexture(this.context.TEXTURE_2D, texture);
                this.context.texParameteri(this.context.TEXTURE_2D, this.context.TEXTURE_MIN_FILTER, this.context.LINEAR);
                this.context.texParameteri(this.context.TEXTURE_2D, this.context.TEXTURE_MAG_FILTER, this.context.LINEAR);
                this.context.texParameteri(this.context.TEXTURE_2D, this.context.TEXTURE_WRAP_S, this.context.CLAMP_TO_EDGE);
                this.context.texParameteri(this.context.TEXTURE_2D, this.context.TEXTURE_WRAP_T, this.context.CLAMP_TO_EDGE);
                this.context.texImage2D(this.context.TEXTURE_2D, 0, this.context.RGBA, this.context.RGBA, this.context.UNSIGNED_BYTE, this.artistSamplers[i].samplerCanvas!);
                buffers.samplerTextures.push(texture);
            }
        }

        if (this.postProcessing) {
            this.framebuffer = this.context.createFramebuffer();
            this.context.bindFramebuffer(this.context.FRAMEBUFFER, this.framebuffer);
    
            const renderTexture = this.context.createTexture();
            this.context.bindTexture(this.context.TEXTURE_2D, renderTexture);
            this.context.texImage2D(this.context.TEXTURE_2D, 0, this.context.RGBA, this.canvasWidth, this.canvasHeight, 0, this.context.RGBA, this.context.UNSIGNED_BYTE, null);
    
            this.context.texParameteri(this.context.TEXTURE_2D, this.context.TEXTURE_MIN_FILTER, this.context.LINEAR);
            this.context.texParameteri(this.context.TEXTURE_2D, this.context.TEXTURE_MAG_FILTER, this.context.LINEAR);
            this.context.texParameteri(this.context.TEXTURE_2D, this.context.TEXTURE_WRAP_S, this.context.CLAMP_TO_EDGE);
            this.context.texParameteri(this.context.TEXTURE_2D, this.context.TEXTURE_WRAP_T, this.context.CLAMP_TO_EDGE);
    
            this.context.framebufferTexture2D(this.context.FRAMEBUFFER, this.context.COLOR_ATTACHMENT0, this.context.TEXTURE_2D, renderTexture, 0);
            
            if (this.shapes) {
                this.depthBuffer = this.context.createRenderbuffer();
                this.context.bindRenderbuffer(this.context.RENDERBUFFER, this.depthBuffer);
                this.context.renderbufferStorage(this.context.RENDERBUFFER, this.context.DEPTH_COMPONENT16, this.canvasWidth, this.canvasHeight);
                this.context.framebufferRenderbuffer(this.context.FRAMEBUFFER, this.context.DEPTH_ATTACHMENT, this.context.RENDERBUFFER, this.depthBuffer);
            } else if (this.depthBuffer) {
                this.context.bindFramebuffer(this.context.FRAMEBUFFER, this.framebuffer);
                this.context.framebufferRenderbuffer(this.context.FRAMEBUFFER, this.context.DEPTH_ATTACHMENT, this.context.RENDERBUFFER, null);
                this.context.bindFramebuffer(this.context.FRAMEBUFFER, null);
                this.context.deleteRenderbuffer(this.depthBuffer);
                this.depthBuffer = undefined;
            }

            if (this.context.checkFramebufferStatus(this.context.FRAMEBUFFER) !== this.context.FRAMEBUFFER_COMPLETE) {
                console.error("Error configuring the framebuffer");
                return;
            }
            
            this.context.bindFramebuffer(this.context.FRAMEBUFFER, null);
            buffers.frameBufferTexture = renderTexture;
            //this.context.bindTexture(this.context.TEXTURE_2D, null);
        }
        return buffers;
    }

    setUniforms(programInfo: ProgramInfo, {time, canvasWidth, canvasHeight, cursorX, cursorY, state, anchorTime, pressure, audioReactiveParameters, parameters, particleX, particleY}: AnimationParameters) {
        this.context.uniform2f(programInfo!.uniformLocations.canvasSize, canvasWidth, canvasHeight);
        this.context.uniform2f(programInfo!.uniformLocations.cursorPosition, cursorX, cursorY);
        this.context.uniform1f(programInfo!.uniformLocations.pointerPressure, pressure);
        if (parameters) {
            parameters.forEach((value, index) => {
                this.context.uniform1f(programInfo!.uniformLocations[`param${index}`], value.value);
            })
        }
        if (audioReactiveParameters) {
            this.context.uniform1i(programInfo!.uniformLocations.audioReactiveOn, 1);
            this.context.uniform1i(programInfo!.uniformLocations.beatDetected, +audioReactiveParameters.beatDetected);
            this.context.uniform1i(programInfo!.uniformLocations.beatCount, audioReactiveParameters.beatCount);
            this.context.uniform1f(programInfo!.uniformLocations.beatFade, audioReactiveParameters.beatFade);
            this.context.uniform1f(programInfo!.uniformLocations.energy, audioReactiveParameters.energy);
        } else {
            this.context.uniform1i(programInfo!.uniformLocations.audioReactiveOn, 0);
        }
        this.context.uniform1i(programInfo!.uniformLocations.state, state);
        this.context.uniform1i(programInfo!.uniformLocations.time, time);
        this.context.uniform1i(programInfo!.uniformLocations.anchorTime, anchorTime);
        if (particleX && particleY) this.context.uniform2f(programInfo!.uniformLocations.particlePosition, particleX, particleY);
    }

    drawScene(time: number, particleX?: number, particleY?: number, audioReactiveParameters?: AudioReactiveParameters) {
        this.context.clearColor(0.0, 0.0, 0.0, 0.0);
        this.context.clear(this.context.COLOR_BUFFER_BIT | this.context.DEPTH_BUFFER_BIT);
        
        if (this.postProcessing && this.framebuffer) {
            this.context.bindFramebuffer(this.context.FRAMEBUFFER, this.framebuffer);
        } else {
            this.context.bindFramebuffer(this.context.FRAMEBUFFER, null);
        }
        this.context.viewport(0, 0, this.canvasWidth, this.canvasHeight);

        this.context.clearColor(0.0, 0.0, 0.0, 0.0);
        this.context.clear(this.context.COLOR_BUFFER_BIT | this.context.DEPTH_BUFFER_BIT);

        this.context.useProgram(this.programInfo!.program);
        const pressure = this.pointerPressure < 0.5 ? 4 * Math.pow(this.pointerPressure, 3) : 1 - Math.pow(-2 * this.pointerPressure + 2, 3) / 2;

        const animationParameters: AnimationParameters = {
            time, 
            canvasWidth: this.canvasWidth,
            canvasHeight: this.canvasHeight,
            cursorX: this.cursorX, 
            cursorY: this.cursorY, 
            state: this.state, 
            anchorTime: this.anchorTime, 
            pressure, 
            audioReactiveParameters, 
            parameters: this.parameters,
            particleX,
            particleY
        };

        this.setUniforms(this.programInfo!, animationParameters)
        //this.context.enable(this.context.BLEND);
        //this.context.blendFunc(this.context.SRC_ALPHA, this.context.ONE_MINUS_SRC_ALPHA);

        this.context.enableVertexAttribArray(this.programInfo!.attribLocations.vertexPosition);
        this.context.bindBuffer(this.context.ARRAY_BUFFER, this.buffers!.position);

        if(this.layoutSampler) {
            this.context.activeTexture(this.context.TEXTURE0);
            this.context.bindTexture(this.context.TEXTURE_2D, this.buffers!.layoutSamplerTexture);
            this.context.texImage2D(this.context.TEXTURE_2D, 0, this.context.RGBA, this.context.RGBA, this.context.UNSIGNED_BYTE, this.layoutSampler);
            this.context.uniform1i(this.programInfo!.uniformLocations.layoutSampler, 0);
        }

        if (this.artistSamplers.length) {
            for (let i = 0; i < this.buffers.samplerTextures.length; i++) {
                const textureUnit = this.context.TEXTURE2 + i;
                this.context.activeTexture(textureUnit);
                this.context.bindTexture(this.context.TEXTURE_2D, this.buffers.samplerTextures[i]);
                this.context.texImage2D(this.context.TEXTURE_2D, 0, this.context.RGBA, this.context.RGBA, this.context.UNSIGNED_BYTE, this.artistSamplers[i].samplerCanvas!);
                this.context.uniform1i(this.programInfo!.uniformLocations[`sampler${i}`], 2 + i);
            }
        }

        this.context.vertexAttribPointer(
            this.programInfo!.attribLocations.vertexPosition,
            3,
            this.context.FLOAT,
            false,
            0,
            0,
        );
        this.context.drawArrays(this.context.TRIANGLE_STRIP, 0, 4);

        if (this.shapes) {
            this.shapeShaders?.forEach(shader => shader.draw(animationParameters))
        }
        
        if (this.postProcessing && this.postProcessingProgramInfo) {
            this.context.enableVertexAttribArray(this.programInfo!.attribLocations.vertexPosition);
            this.context.bindBuffer(this.context.ARRAY_BUFFER, this.buffers!.position);
            this.context.vertexAttribPointer(this.programInfo!.attribLocations.vertexPosition, 3, this.context.FLOAT, false, 0, 0,);
            
            this.context.bindFramebuffer(this.context.FRAMEBUFFER, null);

            this.context.useProgram(this.postProcessingProgramInfo.program);
            this.setUniforms(this.postProcessingProgramInfo, animationParameters)
            this.context.activeTexture(this.context.TEXTURE1);
            this.context.bindTexture(this.context.TEXTURE_2D, this.buffers!.frameBufferTexture);
            this.context.uniform1i(this.postProcessingProgramInfo.uniformLocations.frameBufferSampler, 1);
            this.context.drawArrays(this.context.TRIANGLE_STRIP, 0, 4);
        }

        this.samplers.forEach(async sampler => {
            if (sampler.states.includes(this.state)) {
                switch(sampler.sampleType) {
                    case "Self":
                        sampler.samplerCtx!.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
                        sampler.samplerCtx!.drawImage(sampler.sampleSource!, 0, 0);
                        break;
                    case "Canvas":
                        sampler.samplerCtx!.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
                        sampler.samplerCtx!.drawImage(sampler.sampleSource!, 0, 0, this.canvasWidth, this.canvasHeight);
                        break;
                    case "Camera":
                        sampler.samplerCtx!.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
                        if (sampler.sampleSource instanceof HTMLVideoElement) {
                            if (sampler.sampleSource.srcObject) {
                                const canvasWidth = sampler.samplerCanvas!.width;
                                const canvasHeight = sampler.samplerCanvas!.height;
                                const videoWidth = sampler.sampleSource.videoWidth;
                                const videoHeight = sampler.sampleSource.videoHeight;
                                const canvasRatio = canvasWidth / canvasHeight;
                                const videoRatio = videoWidth / videoHeight;
                                let sx, sy, sWidth, sHeight;
                                let dx, dy, dWidth, dHeight;
                                if (videoRatio > canvasRatio) {
                                    sHeight = videoHeight;
                                    sWidth = videoHeight * canvasRatio;
                                    sx = (videoWidth - sWidth) / 2;
                                    sy = 0;
                                    dx = 0;
                                    dy = 0;
                                    dWidth = canvasWidth;
                                    dHeight = canvasHeight;
                                } else {
                                    sWidth = videoWidth;
                                    sHeight = videoWidth / canvasRatio;
                                    sx = 0;
                                    sy = (videoHeight - sHeight) / 2;
                                    dx = 0;
                                    dy = 0;
                                    dWidth = canvasWidth;
                                    dHeight = canvasHeight;
                                }
                                sampler.samplerCtx!.save();
                                sampler.samplerCtx!.translate(canvasWidth, 0);
                                sampler.samplerCtx!.scale(-1, 1);
                                sampler.samplerCtx!.drawImage(sampler.sampleSource!, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight);
                                sampler.samplerCtx!.restore();
                            } else {
                                sampler.samplerCtx!.drawImage(this.layoutSampler!, 0, 0);
                            }
                        }
                        break;
                    case "Function":
                        if (sampler.samplerFunction) sampler.samplerFunction({
                            ...animationParameters,
                            context: sampler.samplerCtx!,
                            storage: sampler.samplerStorage!,
                        })
                        break;
                    case "BodySegmentation":
                        const bodySegmentationOn = this.parameters?.find(parameter => parameter.name === 'Body Tracking');
                        if (sampler.samplerStorage && sampler.sampleSource instanceof HTMLVideoElement && sampler.sampleSource.srcObject && bodySegmentationOn?.value) {
                            if (!sampler.samplerStorage.segmenter) {
                                sampler.samplerStorage.segmenter = 'Creating Segmenter';
                                const model = bodySegmentation.SupportedModels.BodyPix;
                                const segmenter = await bodySegmentation.createSegmenter(model, {
                                    architecture: 'ResNet50',
                                    outputStride: 32,
                                    quantBytes: 2
                                });
                                const offScreenCanvas = document.createElement('canvas');
                                const offScreenCanvasCtx = offScreenCanvas.getContext('2d');
                                sampler.samplerStorage = {segmenter, offScreenCanvas, offScreenCanvasCtx};
                            } else if (sampler.samplerStorage.segmenter != 'Creating Segmenter') {
                                const storage = sampler.samplerStorage as {
                                    segmenter: bodySegmentation.BodySegmenter,
                                    offScreenCanvas: HTMLCanvasElement,
                                    offScreenCanvasCtx: CanvasRenderingContext2D
                                }
                                const segmentationConfig = {multiSegmentation: true, segmentBodyParts: false};
                                const segmentation = await storage.segmenter.segmentPeople(sampler.sampleSource, segmentationConfig);
                                const coloredPartImage = await bodySegmentation.toBinaryMask(segmentation, {r:0, g:255, b:0, a: 255}, {r:255, g:0, b:0, a: 255});
                                if (sampler.sampleSource.srcObject && coloredPartImage) {
                                    storage.offScreenCanvas.width = coloredPartImage.width;
                                    storage.offScreenCanvas.height = coloredPartImage.height;
                                    storage.offScreenCanvasCtx.putImageData(coloredPartImage, 0, 0);
                                    const canvasWidth = sampler.samplerCanvas!.width;
                                    const canvasHeight = sampler.samplerCanvas!.height;
                                    const videoWidth = coloredPartImage.width;
                                    const videoHeight = coloredPartImage.height;
                                    const canvasRatio = canvasWidth / canvasHeight;
                                    const videoRatio = videoWidth / videoHeight;
                                    let sx, sy, sWidth, sHeight;
                                    let dx, dy, dWidth, dHeight;
                                    if (videoRatio > canvasRatio) {
                                        sHeight = videoHeight;
                                        sWidth = videoHeight * canvasRatio;
                                        sx = (videoWidth - sWidth) / 2;
                                        sy = 0;
                                        dx = 0;
                                        dy = 0;
                                        dWidth = canvasWidth;
                                        dHeight = canvasHeight;
                                    } else {
                                        sWidth = videoWidth;
                                        sHeight = videoWidth / canvasRatio;
                                        sx = 0;
                                        sy = (videoHeight - sHeight) / 2;
                                        dx = 0;
                                        dy = 0;
                                        dWidth = canvasWidth;
                                        dHeight = canvasHeight;
                                    }
                                    sampler.samplerCtx!.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
                                    sampler.samplerCtx!.save();
                                    sampler.samplerCtx!.translate(canvasWidth, 0);
                                    sampler.samplerCtx!.scale(-1, 1);
                                    sampler.samplerCtx!.drawImage(storage.offScreenCanvas, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight);
                                    sampler.samplerCtx!.restore();
                                }
                            }
                        } else {
                            if (sampler.samplerStorage && sampler.samplerStorage.segmenter) sampler.samplerStorage.segmenter = undefined;
                            sampler.samplerCtx!.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
                            if (sampler.sampleSource instanceof HTMLVideoElement && !sampler.sampleSource.srcObject) sampler.samplerCtx!.drawImage(this.layoutSampler!, 0, 0);
                        }
                        break;
                }
            }
        })
    }
}

export default ArtistWebgl


type ProgramInfo = {
    program: WebGLProgram | null,
    attribLocations: any,
    uniformLocations: any,
}

class ShapeShader {
    context: WebGLRenderingContext;

    shapes: Array<Shape>;
    vsSource: string;
    fsSource: string;
    bgFsSource?: string;
    overlayFsSource?: string;
    displayMode: ShapeDisplayMode;
    shapeSamplers: Array<Sampler>;
    parameters?: Array<SceneParameter>;
    shapeAnimation?: (animationParameters: AnimationParameters) => void;

    programInfo: ProgramInfo;
    bgProgramInfo?: ProgramInfo;
    overlayProgramInfo?: ProgramInfo;
    buffers: any;

    constructor(
        context: WebGLRenderingContext, 
        shapes: Array<Shape>, 
        vertexShader: string = basicShapeVs, 
        fragmentShader: string, displayMode: ShapeDisplayMode = "solid", 
        bgShader?: string, 
        overlayFsSource?: string, 
        shapeSamplers: Array<Sampler> = [],
        parameters?: Array<SceneParameter>,
        shapeAnimation?: (animationParameters: AnimationParameters) => void,
    ) {
        this.context = context;
        this.shapes = shapes;
        this.vsSource = vertexShader;
        this.fsSource = fragmentShader;
        this.bgFsSource = bgShader;
        this.overlayFsSource = overlayFsSource;
        this.displayMode = displayMode;
        this.shapeSamplers = shapeSamplers;
        this.parameters = parameters;
        this.shapeAnimation = shapeAnimation;

        this.programInfo = this.initShaderProgram(this.vsSource, this.fsSource)
        this.buffers = this.initBuffers();

        if (this.displayMode == "wireframe" || this.displayMode == "overlay" || this.displayMode == "wireframeAndOverlay") {
            this.bgProgramInfo = this.initShaderProgram(this.vsSource, this.bgFsSource!);
        };

        if (this.displayMode == "overlay" || this.displayMode == "wireframeAndOverlay") {
            this.overlayProgramInfo = this.initShaderProgram(this.vsSource, this.overlayFsSource!);
        };
    }


    loadProgram(vertexShaderSource: string, fragmentShaderSource: string) {
        const loadShader = (type: number, source: string) => {
            const shader = this.context.createShader(type);
            this.context.shaderSource(shader!, source);
            this.context.compileShader(shader!);
            if (!this.context.getShaderParameter(shader!, this.context.COMPILE_STATUS)) {
                alert(
                    `An error occurred compiling the shaders: ${this.context.getShaderInfoLog(shader!)}`,
                );
                this.context.deleteShader(shader);
                return null;
            }
            return shader;
        }
        const vertexShader = loadShader(this.context.VERTEX_SHADER, vertexShaderSource);
        const fragmentShader = loadShader(this.context.FRAGMENT_SHADER, fragmentShaderSource);
        const shaderProgram = this.context.createProgram();
        this.context.attachShader(shaderProgram!, vertexShader!);
        this.context.attachShader(shaderProgram!, fragmentShader!);
        this.context.linkProgram(shaderProgram!);
        if (!this.context.getProgramParameter(shaderProgram!, this.context.LINK_STATUS)) {
            alert(
                `Unable to initialize the shader program: ${this.context.getProgramInfoLog(
                shaderProgram!,
                )}`,
            );
            return null;
        }
        return shaderProgram;
    }

    initShaderProgram(vertexShaderSource: string, fragmentShaderSource: string) {
        const shaderProgram = this.loadProgram(vertexShaderSource, fragmentShaderSource);
        const numParameters = this.parameters? this.parameters.length : 0;
        const numSamplers = this.shapeSamplers.length;
        return {
            program: shaderProgram,
            attribLocations: {
                vertexPosition: this.context.getAttribLocation(shaderProgram!, "a_vertexPosition"),
                vertexNormal: this.context.getAttribLocation(shaderProgram!, "a_vertexNormal"),
                vertexTexCoords: this.context.getAttribLocation(shaderProgram!, "a_texCoords"),
            },
            uniformLocations: {
                ...ArtistWebgl.getCommonUniformLocations(this.context, shaderProgram!, numParameters, numSamplers),
                projectionMatrix: this.context.getUniformLocation(shaderProgram!, "u_projectionMatrix"),
                modelViewMatrix: this.context.getUniformLocation(shaderProgram!, "u_modelViewMatrix"),
                normalMatrix: this.context.getUniformLocation(shaderProgram!, "u_normalMatrix")
            }
        }
    }

    initBuffers() {
        let positions: Array<number> = [];
        let indices: Array<number> = [];
        let normals: Array<number> = [];
        let texCoords: Array<number> = [];
        let samplerTextures: Array<WebGLTexture | null> = [];
        let indexOffset = 0;

        this.shapes.forEach((shape, i) => {
            const mesh = shape.mesh;
            positions = mesh.uniquePositionsBuffer;
            indices = mesh.uniqueIndicesBuffer;
            normals = mesh.uniqueVertexNormalsBuffer;
            texCoords = mesh.texCoordsBuffer;
            indexOffset += mesh.uniquePositionsBuffer.length / 3;
        });

        const positionBuffer = this.context.createBuffer();
        this.context.bindBuffer(this.context.ARRAY_BUFFER, positionBuffer);
        this.context.bufferData(this.context.ARRAY_BUFFER, new Float32Array(positions), this.context.STATIC_DRAW);
        const indexBuffer = this.context.createBuffer();
        this.context.bindBuffer(this.context.ELEMENT_ARRAY_BUFFER, indexBuffer);
        this.context.bufferData(this.context.ELEMENT_ARRAY_BUFFER, new Uint16Array(indices), this.context.STATIC_DRAW);
        const normalBuffer = this.context.createBuffer();
        this.context.bindBuffer(this.context.ARRAY_BUFFER, normalBuffer);
        this.context.bufferData(this.context.ARRAY_BUFFER, new Float32Array(normals), this.context.STATIC_DRAW);
        const texCoordsBuffer = this.context.createBuffer();
        this.context.bindBuffer(this.context.ARRAY_BUFFER, texCoordsBuffer);
        this.context.bufferData(this.context.ARRAY_BUFFER, new Float32Array(texCoords), this.context.STATIC_DRAW);

        if(this.shapeSamplers.length) {
            samplerTextures = [];
            for (let i = 0; i < this.shapeSamplers.length; i++) {
                const texture = this.context.createTexture();
                this.context.bindTexture(this.context.TEXTURE_2D, texture);
                this.context.texParameteri(this.context.TEXTURE_2D, this.context.TEXTURE_MIN_FILTER, this.context.LINEAR);
                this.context.texParameteri(this.context.TEXTURE_2D, this.context.TEXTURE_MAG_FILTER, this.context.LINEAR);
                this.context.texParameteri(this.context.TEXTURE_2D, this.context.TEXTURE_WRAP_S, this.context.CLAMP_TO_EDGE);
                this.context.texParameteri(this.context.TEXTURE_2D, this.context.TEXTURE_WRAP_T, this.context.CLAMP_TO_EDGE);
                this.context.texImage2D(this.context.TEXTURE_2D, 0, this.context.RGBA, this.context.RGBA, this.context.UNSIGNED_BYTE, this.shapeSamplers[i].samplerCanvas!);
                samplerTextures.push(texture);
            }
        }

        return {position: positionBuffer, indices: indexBuffer, normal: normalBuffer, texCoords: texCoordsBuffer, numIndices: indices.length, samplerTextures: samplerTextures};
    }

    setAttributes(buffers: any, programInfo: ProgramInfo) {
        this.context.bindBuffer(this.context.ARRAY_BUFFER, buffers.position);
        this.context.vertexAttribPointer(programInfo.attribLocations.vertexPosition, 3, this.context.FLOAT, false, 0, 0);
        this.context.enableVertexAttribArray(programInfo.attribLocations.vertexPosition);
        this.context.bindBuffer(this.context.ARRAY_BUFFER, buffers.normal);
        this.context.vertexAttribPointer(programInfo.attribLocations.vertexNormal, 3, this.context.FLOAT, false, 0, 0);
        this.context.enableVertexAttribArray(programInfo.attribLocations.vertexNormal);
        this.context.bindBuffer(this.context.ARRAY_BUFFER, buffers.texCoords);
        this.context.vertexAttribPointer(programInfo.attribLocations.vertexTexCoords, 3, this.context.FLOAT, false, 0, 0);
        this.context.enableVertexAttribArray(programInfo.attribLocations.vertexTexCoords)
    }

    setUniforms(programInfo: ProgramInfo, {time, canvasWidth, canvasHeight, cursorX, cursorY, state, anchorTime, pressure, audioReactiveParameters, parameters, particleX, particleY}: AnimationParameters) {
        this.context.uniform2f(this.programInfo!.uniformLocations.canvasSize, canvasWidth, canvasHeight);
        this.context.uniform2f(programInfo!.uniformLocations.cursorPosition, cursorX, cursorY);
        this.context.uniform1f(programInfo!.uniformLocations.pointerPressure, pressure);
        if (parameters) {
            parameters.forEach((value, index) => {
                this.context.uniform1f(programInfo!.uniformLocations[`param${index}`], value.value);
            })
        }
        if (audioReactiveParameters) {
            this.context.uniform1i(programInfo!.uniformLocations.audioReactiveOn, 1);
            this.context.uniform1i(programInfo!.uniformLocations.beatDetected, +audioReactiveParameters.beatDetected);
            this.context.uniform1i(programInfo!.uniformLocations.beatCount, audioReactiveParameters.beatCount);
            this.context.uniform1f(programInfo!.uniformLocations.beatFade, audioReactiveParameters.beatFade);
            this.context.uniform1f(programInfo!.uniformLocations.energy, audioReactiveParameters.energy);
        } else {
            this.context.uniform1i(programInfo!.uniformLocations.audioReactiveOn, 0);
        }
        this.context.uniform1i(programInfo!.uniformLocations.state, state);
        this.context.uniform1i(programInfo!.uniformLocations.time, time);
        this.context.uniform1i(programInfo!.uniformLocations.anchorTime, anchorTime);
        if (particleX && particleY) this.context.uniform2f(this.programInfo!.uniformLocations.particlePosition, particleX, particleY);
    }

    draw(animationParameters: AnimationParameters) {
        if (this.shapeAnimation) this.shapeAnimation(animationParameters);
        const canvasWidth = animationParameters.canvasWidth;
        const canvasHeight = animationParameters.canvasHeight;
        //this.context.frontFace(this.context.CCW);
        this.context.enable(this.context.DEPTH_TEST);
        this.context.depthFunc(this.context.LEQUAL);
        this.context.enable(this.context.BLEND);
        this.context.blendFunc(this.context.SRC_ALPHA, this.context.ONE_MINUS_SRC_ALPHA);

        let indexStart = 0;
        this.shapes.forEach(shape => {
            shape.generateProjectionMatrix(canvasWidth/canvasHeight, undefined, 100, 5000);
            //const projectionMatrix = Mat4.projection(canvasWidth, canvasHeight, 5000);
            //const projectionMatrix = Mat4.perspective(shape.fov, canvasWidth/canvasHeight, 0.1, 5000)
            //let modelViewMatrix = Mat4.translation(shape.position.x, shape.position.y, shape.position.z);

            //modelViewMatrix = modelViewMatrix.rotate(shape.initialRotation.x, shape.initialRotation.y, shape.initialRotation.z);
            //modelViewMatrix = modelViewMatrix.translate(shape.movement.x * canvasWidth, shape.movement.y * canvasHeight, shape.movement.z);
            //modelViewMatrix = modelViewMatrix.rotate(shape.rotation.x, shape.rotation.y, shape.rotation.z);
            //modelViewMatrix = modelViewMatrix.scale(shape.scale.x, shape.scale.y, shape.scale.z);
            
            let normalMatrix = shape.modelViewMatrix.inverse();
            normalMatrix = normalMatrix.transpose();

            const numIndices = shape.mesh.uniqueIndicesBuffer.length;

            let wireframeModelViewMatrix = shape.modelViewMatrix;
            if (this.displayMode == "wireframe" || this.displayMode == "wireframeAndOverlay") {
                this.setAttributes(this.buffers, this.bgProgramInfo!);
                this.context.bindBuffer(this.context.ELEMENT_ARRAY_BUFFER, this.buffers.indices);
                this.context.useProgram(this.bgProgramInfo!.program);
                this.context.uniformMatrix4fv(this.bgProgramInfo!.uniformLocations.projectionMatrix, false, shape.projectionMatrix.mat4);
                this.context.uniformMatrix4fv(this.bgProgramInfo!.uniformLocations.modelViewMatrix, false, shape.modelViewMatrix.mat4);
                this.context.uniformMatrix4fv(this.bgProgramInfo!.uniformLocations.normalMatrix, false, normalMatrix.mat4);
                this.setUniforms(this.bgProgramInfo!, animationParameters);

                if (this.shapeSamplers.length) {
                    for (let i = 0; i < this.buffers.samplerTextures.length; i++) {
                        const textureUnit = this.context.TEXTURE2 + i;
                        this.context.activeTexture(textureUnit);
                        this.context.bindTexture(this.context.TEXTURE_2D, this.buffers.samplerTextures[i]);
                        this.context.texImage2D(this.context.TEXTURE_2D, 0, this.context.RGBA, this.context.RGBA, this.context.UNSIGNED_BYTE, this.shapeSamplers[i].samplerCanvas!);
                        this.context.uniform1i(this.bgProgramInfo!.uniformLocations[`sampler${i}`], 2 + i);
                    }
                }
        
                this.context.drawElements(this.context.TRIANGLES, this.buffers.numIndices, this.context.UNSIGNED_SHORT, 0);
                wireframeModelViewMatrix = shape.modelViewMatrix.scale(shape.wireframeScale, shape.wireframeScale, shape.wireframeScale);
                //shape.modelViewMatrix = shape.modelViewMatrix.scale(shape.wireframeScale, shape.wireframeScale, shape.wireframeScale);
            }
            this.setAttributes(this.buffers, this.programInfo);
            this.context.bindBuffer(this.context.ELEMENT_ARRAY_BUFFER, this.buffers.indices);
            this.context.useProgram(this.programInfo.program);
            this.context.uniformMatrix4fv(this.programInfo.uniformLocations.projectionMatrix, false, shape.projectionMatrix.mat4);
            this.context.uniformMatrix4fv(this.programInfo.uniformLocations.modelViewMatrix, false, wireframeModelViewMatrix.mat4);
            this.context.uniformMatrix4fv(this.programInfo.uniformLocations.normalMatrix, false, normalMatrix.mat4);
            this.setUniforms(this.programInfo!, animationParameters);

            if (this.shapeSamplers.length) {
                for (let i = 0; i < this.buffers.samplerTextures.length; i++) {
                    const textureUnit = this.context.TEXTURE2 + i;
                    this.context.activeTexture(textureUnit);
                    this.context.bindTexture(this.context.TEXTURE_2D, this.buffers.samplerTextures[i]);
                    this.context.texImage2D(this.context.TEXTURE_2D, 0, this.context.RGBA, this.context.RGBA, this.context.UNSIGNED_BYTE, this.shapeSamplers[i].samplerCanvas!);
                    this.context.uniform1i(this.programInfo!.uniformLocations[`sampler${i}`], 2 + i);
                }
            }

            this.context.drawElements(this.context.TRIANGLES, this.buffers.numIndices, this.context.UNSIGNED_SHORT, 0);

            if (this.displayMode == "overlay" || this.displayMode == "wireframeAndOverlay") {
                this.context.depthMask(false);
                const overlayModelViewMatrix = shape.modelViewMatrix.scale(shape.overlayScale, shape.overlayScale, shape.overlayScale);
                //shape.modelViewMatrix = shape.modelViewMatrix.scale(shape.overlayScale, shape.overlayScale, shape.overlayScale);
                this.setAttributes(this.buffers, this.overlayProgramInfo!);
                this.context.bindBuffer(this.context.ELEMENT_ARRAY_BUFFER, this.buffers.indices);
                this.context.useProgram(this.overlayProgramInfo!.program);
                this.context.uniformMatrix4fv(this.overlayProgramInfo!.uniformLocations.projectionMatrix, false, shape.projectionMatrix.mat4);
                this.context.uniformMatrix4fv(this.overlayProgramInfo!.uniformLocations.modelViewMatrix, false, overlayModelViewMatrix.mat4);
                this.context.uniformMatrix4fv(this.overlayProgramInfo!.uniformLocations.normalMatrix, false, normalMatrix.mat4);
                this.setUniforms(this.overlayProgramInfo!, animationParameters);

                if (this.shapeSamplers.length) {
                    for (let i = 0; i < this.buffers.samplerTextures.length; i++) {
                        const textureUnit = this.context.TEXTURE2 + i;
                        this.context.activeTexture(textureUnit);
                        this.context.bindTexture(this.context.TEXTURE_2D, this.buffers.samplerTextures[i]);
                        this.context.texImage2D(this.context.TEXTURE_2D, 0, this.context.RGBA, this.context.RGBA, this.context.UNSIGNED_BYTE, this.shapeSamplers[i].samplerCanvas!);
                        this.context.uniform1i(this.overlayProgramInfo!.uniformLocations[`sampler${i}`], 2 + i);
                    }
                }

                this.context.drawElements(this.context.TRIANGLES, this.buffers.numIndices, this.context.UNSIGNED_SHORT, 0);
                this.context.depthMask(true);
            }

            indexStart += numIndices;
        });
    }
}